import { FC, useRef, useEffect, useState } from 'react';
import {
  Participant as TwilioParticipant,
  RemoteAudioTrack,
  RemoteVideoTrack,
  LocalAudioTrack,
  LocalVideoTrack,
} from 'twilio-video';
import { cn } from '@new/lib';

type AudioTrack = RemoteAudioTrack | LocalAudioTrack;
type VideoTrack = RemoteVideoTrack | LocalVideoTrack;

interface VideoParticipantProps {
  participant: TwilioParticipant;
  isLocal?: boolean;
  isSpeakerEnabled?: boolean;
  className?: string;
  videoId?: string;
  'data-pip-video'?: boolean;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
}

export const VideoParticipant: FC<VideoParticipantProps> = ({
  participant,
  isLocal = false,
  isSpeakerEnabled = true,
  className,
  videoId,
  'data-pip-video': dataPipVideo,
  onVideoRef,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const isMutedRef = useRef(isLocal || !isSpeakerEnabled);
  const [videoTracks, setVideoTracks] = useState<VideoTrack[]>([]);
  const [audioTracks, setAudioTracks] = useState<AudioTrack[]>([]);

  useEffect(() => {
    const trackSubscribed = (track: AudioTrack | VideoTrack) => {
      if (track.kind === 'video') {
        setVideoTracks((prev) => [...prev, track as VideoTrack]);
      } else {
        setAudioTracks((prev) => [...prev, track as AudioTrack]);
      }
    };

    const trackUnsubscribed = (track: AudioTrack | VideoTrack) => {
      if (track.kind === 'video') {
        setVideoTracks((prev) => prev.filter((v) => v !== track));
      } else {
        setAudioTracks((prev) => prev.filter((a) => a !== track));
      }
    };

    const existingVideo = Array.from(participant.videoTracks.values())
      .map((pub) => (pub as any).track)
      .filter(Boolean) as VideoTrack[];
    const existingAudio = Array.from(participant.audioTracks.values())
      .map((pub) => (pub as any).track)
      .filter(Boolean) as AudioTrack[];

    setVideoTracks(existingVideo);
    setAudioTracks(existingAudio);

    participant.on('trackSubscribed', trackSubscribed);
    participant.on('trackUnsubscribed', trackUnsubscribed);

    return () => {
      setVideoTracks([]);
      setAudioTracks([]);
      (participant as any).off('trackSubscribed', trackSubscribed);
      (participant as any).off('trackUnsubscribed', trackUnsubscribed);
    };
  }, [participant]);

  useEffect(() => {
    const videoTrack = videoTracks[0];
    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current);
      videoRef.current.muted = true;
      return () => {
        videoTrack.detach();
      };
    }
  }, [videoTracks]);

  isMutedRef.current = isLocal || !isSpeakerEnabled;

  useEffect(() => {
    const audioTrack = audioTracks[0];
    const el = audioRef.current;
    if (audioTrack && el) {
      audioTrack.attach(el);
      el.muted = isMutedRef.current;
      el.play().catch((err) => console.warn('[VideoParticipant] play() failed', err));
      return () => {
        audioTrack.detach();
      };
    }
  }, [audioTracks]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isLocal || !isSpeakerEnabled;
    }
  }, [isLocal, isSpeakerEnabled]);

  return (
    <div
      className={cn('relative overflow-hidden rounded-lg bg-neutral-900', className)}
      {...(dataPipVideo ? { 'data-pip-video': '' } : {})}
    >
      <video
        ref={(el) => {
          (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
          onVideoRef?.(el);
        }}
        autoPlay
        playsInline
        id={videoId}
        className="w-full h-full object-cover"
      />
      <audio ref={audioRef} autoPlay />
      <div className="absolute bottom-3 left-3 bg-black/60 text-white text-sm px-3 py-1 rounded-md">
        {participant.identity}
      </div>
    </div>
  );
};
